import { ColumnDef } from "@tanstack/table-core";
import moment from "moment";
import Table from "../../../components/Table";
import { RefundRecord } from "../../../types/refund";
import { usdFormat } from "../../../utils";

const typeLabel: Record<string, string> = {
  void: "Void",
  full: "Full refund",
  line_item: "Line-item refund",
  external: "Refund from the processor",
};

const actionLabel: Record<string, string> = {
  void: "charge cancelled",
  return: "money returned",
  balance: "band credited",
  cash: "recorded only",
};

const itemsSummary = (items: RefundRecord["items"]) => {
  if (!Array.isArray(items) || items.length === 0) return "-";
  const counts = new Map<string, number>();
  items.forEach((unit) => {
    const name = unit?.name || "item";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, count]) => `${count}x ${name}`)
    .join(", ");
};

const columns: ColumnDef<RefundRecord>[] = [
  {
    header: "When",
    accessorKey: "created_at",
    cell: (info) => moment(info.getValue<string>()).format("lll"),
  },
  {
    header: "Type",
    accessorKey: "refund_type",
    cell: (info) => typeLabel[info.getValue<string>()] ?? info.getValue<string>(),
  },
  {
    header: "Processor",
    accessorKey: "processor_action",
    cell: (info) => actionLabel[info.getValue<string>()] ?? info.getValue<string>(),
  },
  {
    header: "Amount",
    accessorKey: "amount",
    cell: (info) => usdFormat.format(info.getValue<number>() / 100),
  },
  {
    header: "Tax part",
    accessorKey: "tax_part",
    cell: (info) => usdFormat.format(info.getValue<number>() / 100),
  },
  {
    header: "Items",
    accessorKey: "items",
    cell: (info) => itemsSummary(info.getValue<RefundRecord["items"]>()),
  },
  {
    header: "Reason",
    accessorKey: "reason_code",
    cell: (info) => {
      const row = info.row.original;
      return [row.reason_code, row.reason].filter(Boolean).join(": ") || "-";
    },
  },
  {
    header: "User",
    accessorKey: "user_id",
    cell: (info) => info.getValue<number | null>() ?? "-",
  },
];

export const RefundHistoryTable = ({ refunds }: { refunds: RefundRecord[] }) => {
  if (!refunds?.length) {
    return <p className="text-sm text-gray-500 py-4">No refunds on this order.</p>;
  }
  return <Table columns={columns} data={refunds} />;
};
